import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { symbolData } from "../SymbolData";
import { FEATURES_CONFIG } from "../featuresConfig";

/**
 * SpineTextureExtractor
 *
 * Replaces traditional spritesheets by extracting static frames from symbol
 * spine animations at load time. Produces PIXI.Texture objects (normal and
 * blurred variants) used during reel spinning.
 *
 * How it works:
 *   1. For each symbol in symbolData that has a spine file, instantiate a
 *      temporary Spine, select the best animation (idle → loop → win →
 *      first available), and render frame 0 into a RenderTexture —
 *      this becomes the reel symbol texture.
 *   2. Optionally, post-process each texture with a vertical squash + blur
 *      to generate blurred variants for the spin motion effect.
 *
 * Key design decisions:
 *   - Textures are origin-centered: the spine's (0,0) maps to the texture
 *     center, so sprite.anchor(0.5) aligns perfectly with the live spine
 *     overlay during win animations (no visual "bounce" on transition).
 *   - Animation selection is automatic via priority fallback — no specific
 *     animation name is required in the spine file.
 *   - All results are cached — subsequent calls return instantly.
 *
 * Output format (drop-in replacement for PIXI.Spritesheet):
 *   { textures: { "w1.png": Texture, "h1.png": Texture, ... } }
 *   { textures: { "w1_blurred.png": Texture, ... } }   // blurred variant
 */
class SpineTextureExtractor {
  // Module-level singleton — initialized once, consumed everywhere
  static _instance = null;

  /**
   * One-time initialization. Call once when renderer is available.
   * Extracts all symbol textures, blurred variants, and generates fonts.
   */
  static initialize(renderer) {
    if (SpineTextureExtractor._instance) return;

    const extractor = new SpineTextureExtractor(renderer);
    extractor.extractAll();

    if (FEATURES_CONFIG.SPIN.BLURRED_TEXTURE_GENERATION?.ENABLED) {
      extractor.extractAllBlurred();
    }

    SpineTextureExtractor._instance = extractor;
  }

  /** @returns {{ textures: Record<string, PIXI.RenderTexture> } | null} */
  static getSymbols() {
    return SpineTextureExtractor._instance?.extractAll() ?? null;
  }

  /** @returns {{ textures: Record<string, PIXI.RenderTexture> } | null} */
  static getBlurredSymbols() {
    if (!FEATURES_CONFIG.SPIN.BLURRED_TEXTURES_ENABLED) return null;
    return SpineTextureExtractor._instance?.extractAllBlurred() ?? null;
  }

  /** @returns {Record<string, { src: string, width: number, height: number }> | null} */
  static getDataURLs() {
    return SpineTextureExtractor._instance?.extractAllAsDataURLs() ?? null;
  }

  constructor(renderer) {
    this.renderer = renderer;
    this._textureCache = null;
    this._dataURLCache = null;
    this._blurredTextureCache = null;
  }

  /**
   * Extract normal (sharp) textures for all symbols.
   * Iterates symbolData, deduplicates shared spines, and produces one
   * texture per unique symbol (including per-skin variants like l1–l6).
   * @returns {{ textures: Record<string, PIXI.RenderTexture> }}
   */
  extractAll() {
    if (this._textureCache) return this._textureCache;

    const textures = {};
    const processed = new Set();

    for (const [, symbolInfo] of Object.entries(symbolData)) {
      if (
        !symbolInfo?.spine ||
        symbolInfo.spine === "" ||
        typeof symbolInfo.spine !== "string"
      ) {
        continue;
      }

      const textureName = symbolInfo.texture;
      if (!textureName || processed.has(textureName)) {
        continue;
      }

      const texture = this._extractSingleFrame(
        symbolInfo.spine,
        symbolInfo.skin || null,
      );

      if (texture) {
        textures[`${textureName}.png`] = texture;
        processed.add(textureName);
      }
    }

    this._textureCache = { textures };
    return this._textureCache;
  }

  /**
   * Convert extracted textures to base64 data URLs for HTML <img> tags
   * (used in paytable and game rules screens).
   * @returns {Record<string, { src: string, width: number, height: number }>}
   */
  extractAllAsDataURLs() {
    if (this._dataURLCache) return this._dataURLCache;

    const spritesheet = this.extractAll();
    if (!spritesheet?.textures) return {};

    const result = {};
    for (const [key, texture] of Object.entries(spritesheet.textures)) {
      const name = key.replace(/\.png$/, "");
      try {
        const canvas = this.renderer.extract.canvas(texture);
        if (canvas) {
          result[name] = {
            src: canvas.toDataURL("image/png"),
            width: canvas.width,
            height: canvas.height,
          };
        }
      } catch {
        // Skip failed extractions
      }
    }

    this._dataURLCache = result;
    return result;
  }

  /**
   * Generate blurred variants of all symbol textures.
   * Takes each normal texture from extractAll(), applies a vertical squash
   * and Gaussian blur (configured via FEATURES_CONFIG.SPIN.BLURRED_TEXTURE_GENERATION),
   * producing textures used during the spin motion effect.
   * @returns {{ textures: Record<string, PIXI.RenderTexture> }}
   */
  extractAllBlurred() {
    if (this._blurredTextureCache) return this._blurredTextureCache;

    const normalSpritesheet = this.extractAll();
    if (!normalSpritesheet?.textures) return null;

    const textures = {};

    for (const [key, normalTexture] of Object.entries(
      normalSpritesheet.textures,
    )) {
      const blurredKey = key.replace(".png", "_blurred.png");

      try {
        const blurredTexture = this._createBlurredTexture(normalTexture);
        if (blurredTexture) {
          textures[blurredKey] = blurredTexture;
        }
      } catch {
        // Skip failed extractions
      }
    }

    this._blurredTextureCache = { textures };
    return this._blurredTextureCache;
  }

  /**
   * Render a single spine's "static" animation into a RenderTexture.
   *
   * The texture is origin-centered: the spine's design origin (0,0) is placed
   * at the exact center of the texture. This ensures that when a PIXI.Sprite
   * uses anchor(0.5), it aligns pixel-perfectly with the live spine overlay
   * during win animations — eliminating the visual "bounce" that occurs when
   * the static texture's center differs from the spine's origin.
   *
   * @param {string} spineName - Spine asset name (e.g., "lp", "h1", "w1")
   * @param {string|null} skinName - Optional skin to apply (e.g., "l1" for shared spines)
   * @returns {PIXI.RenderTexture|null}
   */
  _extractSingleFrame(spineName, skinName = null) {
    const skeletonAlias = `${spineName}-skeleton`;
    const atlasAlias = `${spineName}-atlas`;
    const assets = PIXI.Assets.cache;

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
      return null;
    }

    let spine = null;
    try {
      spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
        autoUpdate: false,
      });

      // Apply skin for shared spines (e.g., "lp" spine → skins "l1"–"l6")
      if (
        FEATURES_CONFIG.SPINE_ANIMATIONS.SHARED_SPINE_SKINS &&
        skinName &&
        spine.skeleton
      ) {
        spine.skeleton.setSkinByName(skinName);
        spine.skeleton.setSlotsToSetupPose();
      }

      // Select the best animation for frame-0 extraction.
      // Priority: idle → loop → win → first available.
      const ANIM_PRIORITY = ["idle", "loop", "win"];
      const animations = spine.skeleton?.data?.animations;
      let selectedAnim = null;

      if (animations?.length) {
        for (const preferred of ANIM_PRIORITY) {
          selectedAnim = animations.find(
            (a) => a.name.toLowerCase() === preferred,
          );
          if (selectedAnim) break;
        }
        // Ultimate fallback: use the first animation available
        if (!selectedAnim) {
          selectedAnim = animations[0];
        }
      }

      if (!selectedAnim) {

        spine.destroy({ children: true });
        return null;
      }

      // Capture at t=0 — the overlay spine also starts with update(0), so both
      // show the exact same pose. Using selectedAnim.duration previously caused
      // subtle differences if the idle animation doesn't loop back perfectly.
      spine.state.setAnimation(0, selectedAnim.name, false);
      spine.state.update(0);
      spine.state.apply(spine.skeleton);

      try {
        if (spine.skeleton.physics !== undefined) {
          spine.skeleton.updateWorldTransform(2);
        } else {
          spine.skeleton.updateWorldTransform(0);
        }
      } catch {
        try {
          spine.skeleton.updateWorldTransform();
        } catch {}
      }

      spine.alpha = 1;

      const bounds = spine.getLocalBounds();
      if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
        spine.destroy({ children: true });
        return null;
      }

      // Origin-centered canvas: size the texture so spine origin (0,0) is at center.
      // This produces a symmetric canvas that aligns with sprite anchor(0.5).
      // +2 safety margin: asymmetric bounds (e.g. -93 to +94) cause the content
      // edge to land exactly at the canvas boundary, clipping 1px.
      const halfW = Math.max(
        Math.abs(bounds.x),
        Math.abs(bounds.x + bounds.width),
      );
      const halfH = Math.max(
        Math.abs(bounds.y),
        Math.abs(bounds.y + bounds.height),
      );
      const texWidth = Math.ceil(halfW * 2) + 16;
      const texHeight = Math.ceil(halfH * 2) + 16;

      const renderTexture = PIXI.RenderTexture.create({
        width: texWidth,
        height: texHeight,
        resolution: this.renderer.resolution,
      });

      // Origin at canvas center — matches sprite anchor(0.5)
      // +1 accounts for the safety margin added to canvas dimensions
      spine.position.set(texWidth / 2, texHeight / 2);
      this.renderer.render(spine, { renderTexture });

      spine.state.clearTracks();
      spine.destroy({ children: true });
      spine = null;

      return renderTexture;
    } catch (error) {

      if (spine) {
        try {
          spine.destroy({ children: true });
        } catch {}
      }
      return null;
    }
  }

  /**
   * Post-process a texture into a blurred variant for spin animation.
   * Applies configurable vertical squash (SCALE_Y) and Gaussian blur
   * (BLUR_STRENGTH) from FEATURES_CONFIG.SPIN.BLURRED_TEXTURE_GENERATION.
   *
   * @param {PIXI.RenderTexture} sourceTexture - Normal extracted texture
   * @returns {PIXI.RenderTexture|null}
   */
  _createBlurredTexture(sourceTexture) {
    if (
      !sourceTexture ||
      sourceTexture.width <= 0 ||
      sourceTexture.height <= 0
    ) {
      return null;
    }

    const config = FEATURES_CONFIG.SPIN.BLURRED_TEXTURE_GENERATION;

    const sprite = new PIXI.Sprite(sourceTexture);
    sprite.anchor.set(0.5);
    sprite.scale.x = config.SCALE_X ?? 1.0;
    sprite.scale.y = config.SCALE_Y ?? 0.8;

    const blurFilter = new PIXI.BlurFilter();
    blurFilter.blurX = 0;
    blurFilter.blurY = config.BLUR_STRENGTH ?? 18;
    blurFilter.quality = 8;
    sprite.filters = [blurFilter];

    const texWidth = sourceTexture.width;
    const texHeight = sourceTexture.height;

    const renderTexture = PIXI.RenderTexture.create({
      width: texWidth,
      height: texHeight,
      resolution: 1,
    });

    sprite.position.set(texWidth / 2, texHeight / 2);
    this.renderer.render(sprite, { renderTexture });

    sprite.filters = null;
    sprite.destroy();

    return renderTexture;
  }

  /** Free all GPU memory held by extracted textures. */
  dispose() {
    if (this._textureCache?.textures) {
      for (const texture of Object.values(this._textureCache.textures)) {
        if (texture && !texture.destroyed) {
          texture.destroy(true);
        }
      }
      this._textureCache = null;
    }

    if (this._blurredTextureCache?.textures) {
      for (const texture of Object.values(this._blurredTextureCache.textures)) {
        if (texture && !texture.destroyed) {
          texture.destroy(true);
        }
      }
      this._blurredTextureCache = null;
    }
  }
}

export default SpineTextureExtractor;
